import type { SessionSummary, ShootTransaction } from '@diana/contracts';
import { financialClient } from '../crm/crm-format.js';

export const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];
export function packageIdentity(shoot: SessionSummary) {
  return shoot.requestedPackageKey
    ? `key:${shoot.requestedPackageKey}`
    : shoot.requestedPackageTitle
      ? `title:${shoot.requestedPackageTitle}`
      : 'without-package';
}
export type DateRange = { start: string; end: string };
export type PeriodPreset =
  'last12' | 'last6' | 'thisMonth' | 'lastMonth' | 'thisYear' | 'lastYear' | 'custom';
export const PERIOD_PRESETS: { value: PeriodPreset; label: string }[] = [
  { value: 'last12', label: 'Last 12 months' },
  { value: 'last6', label: 'Last 6 months' },
  { value: 'thisMonth', label: 'This month' },
  { value: 'lastMonth', label: 'Last month' },
  { value: 'thisYear', label: 'This year' },
  { value: 'lastYear', label: 'Last year' },
  { value: 'custom', label: 'Custom' },
];
function monthStart(year: number, month: number) {
  const date = new Date(0);
  date.setUTCFullYear(year, month, 1);
  date.setUTCHours(0, 0, 0, 0);
  return date.toISOString().slice(0, 10);
}
export function periodRange(preset: PeriodPreset, today: string, custom: DateRange): DateRange {
  const year = Number(today.slice(0, 4)),
    month = Number(today.slice(5, 7)) - 1;
  if (preset === 'custom') return custom;
  if (preset === 'thisMonth') return { start: monthStart(year, month), end: today };
  if (preset === 'lastMonth')
    return {
      start: monthStart(year, month - 1),
      end: new Date(Date.parse(monthStart(year, month)) - 86400000).toISOString().slice(0, 10),
    };
  if (preset === 'thisYear') return { start: `${year}-01-01`, end: today };
  if (preset === 'lastYear') return { start: `${year - 1}-01-01`, end: `${year - 1}-12-31` };
  return { start: monthStart(year, month - (preset === 'last6' ? 5 : 11)), end: today };
}
export function rangeError(range: DateRange) {
  const valid = (value: string) =>
    /^\d{4}-\d{2}-\d{2}$/.test(value) &&
    Number(value.slice(0, 4)) >= 1 &&
    Number.isFinite(Date.parse(value)) &&
    new Date(value).toISOString().slice(0, 10) === value;
  if (!valid(range.start) || !valid(range.end)) return 'Choose a start and end date.';
  if (range.end < range.start) return 'End date must be on or after start date.';
  const count =
    (Number(range.end.slice(0, 4)) - Number(range.start.slice(0, 4))) * 12 +
    Number(range.end.slice(5, 7)) -
    Number(range.start.slice(5, 7)) +
    1;
  return count > 120 ? 'Choose a period of up to 10 years.' : null;
}
export function monthLabel(key: string) {
  return `${MONTHS[Number(key.slice(5, 7)) - 1]} ${key.slice(0, 4)}`;
}
export function dashboardOptions(shoots: readonly SessionSummary[]) {
  return {
    currencies: [...new Set(shoots.length ? shoots.map((s) => s.currency) : ['EUR'])].sort(
      (a, b) => (a === 'EUR' ? -1 : b === 'EUR' ? 1 : a.localeCompare(b)),
    ),
  };
}
export type CashRecord = { shoot: SessionSummary; transaction: ShootTransaction };
export function dashboardFinance(
  shoots: readonly SessionSummary[],
  range: DateRange,
  currency: string,
  month: string | null = null,
) {
  if (rangeError(range)) throw new Error('Invalid dashboard date range');
  const months: { key: string; label: string; income: number; expenses: number }[] = [];
  let cursor = range.start.slice(0, 7);
  while (cursor <= range.end.slice(0, 7)) {
    months.push({ key: cursor, label: monthLabel(cursor), income: 0, expenses: 0 });
    if (cursor === range.end.slice(0, 7)) break;
    cursor = monthStart(Number(cursor.slice(0, 4)), Number(cursor.slice(5, 7))).slice(0, 7);
  }
  const buckets = new Map(months.map((m) => [m.key, m]));
  const records: CashRecord[] = [];
  const packages = new Map<
    string,
    { key: string; title: string; received: number; shoots: Set<string> }
  >();
  const clients = new Map<
    string,
    { id: string; name: string; received: number; shoots: Set<string> }
  >();
  let missing = 0;
  for (const shoot of shoots) {
    if (shoot.currency !== currency) continue;
    if (!shoot.transactions) {
      missing++;
      continue;
    }
    for (const transaction of shoot.transactions) {
      if (transaction.date < range.start || transaction.date > range.end) continue;
      const keyMonth = transaction.date.slice(0, 7);
      const bucket = buckets.get(keyMonth)!;
      if (transaction.kind === 'Income') bucket.income += transaction.amountMinor;
      else bucket.expenses += transaction.amountMinor;
      if (month !== null && keyMonth !== month) continue;
      records.push({ shoot, transaction });
      if (transaction.kind !== 'Income') continue;
      const primary = financialClient(shoot);
      const client = clients.get(primary.id) ?? {
        id: primary.id,
        name: primary.name,
        received: 0,
        shoots: new Set<string>(),
      };
      client.received += transaction.amountMinor;
      client.shoots.add(shoot.id);
      clients.set(primary.id, client);
      const key = packageIdentity(shoot);
      const item = packages.get(key) ?? {
        key,
        title: shoot.requestedPackageTitle ?? 'Without package',
        received: 0,
        shoots: new Set<string>(),
      };
      item.received += transaction.amountMinor;
      item.shoots.add(shoot.id);
      packages.set(key, item);
    }
  }
  const income = records
    .filter((r) => r.transaction.kind === 'Income')
    .reduce((n, r) => n + r.transaction.amountMinor, 0);
  const expenses = records
    .filter((r) => r.transaction.kind === 'Expense')
    .reduce((n, r) => n + r.transaction.amountMinor, 0);
  return {
    months,
    records: records.sort((a, b) => b.transaction.date.localeCompare(a.transaction.date)),
    income,
    expenses,
    net: income - expenses,
    missing,
    clients: [...clients.values()]
      .map((c) => ({ id: c.id, name: c.name, received: c.received, count: c.shoots.size }))
      .sort(
        (a, b) =>
          b.received - a.received || a.name.localeCompare(b.name) || a.id.localeCompare(b.id),
      ),
    packages: [...packages.values()]
      .map((p) => ({ ...p, count: p.shoots.size, share: income ? p.received / income : 0 }))
      .sort((a, b) => b.received - a.received || a.title.localeCompare(b.title)),
  };
}
export function lisbonDate(value: Date | string) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Lisbon',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(value));
}
export function daysSince(value: string, today: string) {
  return Math.max(0, Math.floor((Date.parse(today) - Date.parse(lisbonDate(value))) / 86400000));
}
export function dashboardWork(shoots: readonly SessionSummary[], now: Date) {
  const today = lisbonDate(now);
  const upcoming = shoots
    .filter((s) => s.status === 'Scheduled')
    .sort((a, b) => (a.scheduledStartAt ?? '9999').localeCompare(b.scheduledStartAt ?? '9999'));
  const attention: {
    shoot: SessionSummary;
    reason: string;
    priority: number;
    outstanding: number;
  }[] = [];
  for (const shoot of shoots) {
    if (shoot.status === 'Canceled') continue;
    const reason =
      shoot.status === 'NewRequest'
        ? 'New enquiry'
        : shoot.status === 'Scheduled' && !shoot.scheduledStartAt
          ? 'Date not set'
          : shoot.status === 'Editing'
            ? shoot.scheduledStartAt
              ? lisbonDate(shoot.scheduledStartAt) > today
                ? 'In editing · check shoot date'
                : `${daysSince(shoot.scheduledStartAt, today)} ${daysSince(shoot.scheduledStartAt, today) === 1 ? 'day' : 'days'} since shoot`
              : 'In editing · date not set'
            : shoot.status === 'InDiscussion'
              ? `Discussion · updated ${daysSince(shoot.updatedAt, today)} days ago`
              : null;
    const received = shoot.transactions
      ?.filter((t) => t.kind === 'Income')
      .reduce((n, t) => n + t.amountMinor, 0);
    const outstanding =
      ['Scheduled', 'Editing', 'Delivered'].includes(shoot.status) &&
      shoot.priceAmountMinor !== null &&
      received !== undefined
        ? Math.max(0, shoot.priceAmountMinor - received)
        : 0;
    if (reason || outstanding)
      attention.push({
        shoot,
        outstanding,
        reason: reason ?? 'Payment outstanding',
        priority:
          shoot.status === 'NewRequest'
            ? 0
            : shoot.status === 'Scheduled' && !shoot.scheduledStartAt
              ? 1
              : shoot.status === 'Editing'
                ? 2
                : 3,
      });
  }
  return {
    upcoming,
    attention: attention.sort(
      (a, b) => a.priority - b.priority || a.shoot.updatedAt.localeCompare(b.shoot.updatedAt),
    ),
    counts: [
      shoots.filter((s) => s.status === 'NewRequest').length,
      shoots.filter((s) => s.status === 'InDiscussion').length,
      upcoming.length,
      shoots.filter((s) => s.status === 'Editing').length,
    ],
  };
}
